import React, { useEffect, useMemo, useState } from "react";
import {
  UsersIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  MegaphoneIcon,
  ShieldCheckIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ChartBarSquareIcon,
  Squares2X2Icon,
  ChatBubbleLeftRightIcon,
  IdentificationIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { API_URL } from "../config/connect";
import InstallsAndReport from "./InstallsAndReport";

const formatN = (n) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(n || 0);

const StatCard = ({ label, value, sub, icon: Icon, accent = "blue", suffix }) => {
  const map = {
    blue: "from-blue-500/20 to-blue-500/0 border-blue-500/30 text-blue-400",
    green: "from-green-500/20 to-green-500/0 border-green-500/30 text-green-400",
    amber: "from-amber-500/20 to-amber-500/0 border-amber-500/30 text-amber-400",
    purple: "from-purple-500/20 to-purple-500/0 border-purple-500/30 text-purple-400",
    rose: "from-rose-500/20 to-rose-500/0 border-rose-500/30 text-rose-400",
    cyan: "from-cyan-500/20 to-cyan-500/0 border-cyan-500/30 text-cyan-400",
  }[accent];
  return (
    <div
      className={`dark-card border rounded-2xl p-5 bg-gradient-to-br ${map} hover:scale-[1.01] transition-transform`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        <div className="text-sm text-text-secondary uppercase tracking-wide">{label}</div>
      </div>
      <div className="text-2xl md:text-3xl font-bold text-white">
        {formatN(value)}
        {suffix ? <span className="text-lg text-text-secondary ml-1">{suffix}</span> : null}
      </div>
      {sub ? <div className="text-xs text-text-secondary mt-2">{sub}</div> : null}
    </div>
  );
};

const QuickAction = ({ label, desc, icon: Icon, onClick }) => (
  <button
    onClick={onClick}
    className="dark-card border border-white/5 rounded-xl p-4 flex items-start gap-3 text-left hover:border-green-500/40 transition-colors w-full"
  >
    <div className="w-10 h-10 rounded-lg bg-accent-grad flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div className="min-w-0">
      <div className="text-white text-sm font-semibold">{label}</div>
      <div className="text-xs text-text-secondary mt-0.5 line-clamp-2">{desc}</div>
    </div>
  </button>
);

const AdminHome = ({ userEmail, onNavigate }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("google_id_token");
      const res = await fetch(`${API_URL}/api/v1/admin/analytics/summary`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        setStats(await res.json());
      } else {
        throw new Error(`HTTP ${res.status}`);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const cards = useMemo(
    () => [
      {
        label: "Пайдаланушылар",
        value: stats?.total_users,
        sub: stats?.new_users_7d ? `+${formatN(stats.new_users_7d)} соңғы 7 күнде` : null,
        icon: UsersIcon,
        accent: "blue",
      },
      {
        label: "Активті хабарландырулар",
        value: stats?.active_announcements,
        sub: stats?.pending_announcements
          ? `${formatN(stats.pending_announcements)} модерация күтуде`
          : null,
        icon: DocumentTextIcon,
        accent: "green",
      },
      {
        label: "Транзакциялар (24сағ)",
        value: stats?.transactions_24h,
        sub: stats?.revenue_24h ? `${formatN(stats.revenue_24h)} ₸ айналым` : null,
        icon: CurrencyDollarIcon,
        accent: "amber",
      },
      {
        label: "Активті чаттар",
        value: stats?.active_chats,
        sub: stats?.unread_chats
          ? `${formatN(stats.unread_chats)} оқылмаған`
          : null,
        icon: ChatBubbleLeftRightIcon,
        accent: "purple",
      },
      {
        label: "Withdrawals күтуде",
        value: stats?.pending_withdrawals,
        sub: stats?.pending_withdrawals_amount
          ? `${formatN(stats.pending_withdrawals_amount)} ₸`
          : null,
        icon: ArrowTrendingUpIcon,
        accent: "cyan",
      },
      {
        label: "KYC күтуде",
        value: stats?.pending_kyc,
        icon: IdentificationIcon,
        accent: "rose",
      },
    ],
    [stats]
  );

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-6">
      {/* Hero header */}
      <div className="relative dark-card rounded-2xl p-6 md:p-8 border border-white/5 overflow-hidden">
        <div className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full bg-green-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-16 w-56 h-56 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-text-secondary mb-1">
              Admin console
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white truncate">
              Сәлеметсіз бе{userEmail ? `, ${userEmail.split("@")[0]}` : ""} 👋
            </h1>
            <p className="text-text-secondary mt-1 text-sm">
              Мұнда бүкіл платформа-ның live-күйі: пайдаланушылар, хабарландырулар, қаржы, чат.
            </p>
          </div>
          <button
            onClick={fetchStats}
            disabled={loading}
            className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 text-white/80 hover:bg-white/5 text-sm"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Жаңарту
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          Analytics summary жүктеу қатесі: {error}. Backend endpoint орнатылмағанын
          білдіруі мүмкін — деректер бос көрсетіледі.
        </div>
      )}

      {/* KPI grid */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <ChartBarSquareIcon className="w-5 h-5 text-text-secondary" />
          <h2 className="text-white font-semibold text-lg">Live KPI</h2>
          {loading && (
            <span className="ml-2 text-xs text-text-secondary">жүктелуде...</span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((c) => (
            <StatCard key={c.label} {...c} />
          ))}
        </div>
      </div>

      {/* Android/iOS орнатулар + отчет экспорты (Excel/Word) */}
      <InstallsAndReport />

      {/* Quick actions */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Squares2X2Icon className="w-5 h-5 text-text-secondary" />
          <h2 className="text-white font-semibold text-lg">Жедел әрекеттер</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickAction
            label="Пайдаланушыны қосу"
            desc="Жаңа юзер, модератор не оператор енгізу."
            icon={UsersIcon}
            onClick={() => onNavigate?.("add-user")}
          />
          <QuickAction
            label="Аналитика"
            desc="Marketplace live-KPI және динамика."
            icon={ChartBarSquareIcon}
            onClick={() => onNavigate?.("analytics")}
          />
          <QuickAction
            label="Маркетинг и баннеры"
            desc="Сториз, баннер, рассылка + модерация — бір беттегі 4 таб."
            icon={MegaphoneIcon}
            onClick={() => onNavigate?.("marketing-hub")}
          />
          <QuickAction
            label="Чат мониторинг"
            desc="System чат, тарихы, тексеру."
            icon={ChatBubbleLeftRightIcon}
            onClick={() => onNavigate?.("chat-management")}
          />
          <QuickAction
            label="Тарифтер"
            desc="Баға, мерзім, VIP сағат, лимиттер."
            icon={ShieldCheckIcon}
            onClick={() => onNavigate?.("tariffs")}
          />
          <QuickAction
            label="Баланстар"
            desc="Юзер балансы, top-up, аудит."
            icon={CurrencyDollarIcon}
            onClick={() => onNavigate?.("balance")}
          />
          <QuickAction
            label="App нұсқасы"
            desc="Mobile app force-update, changelog."
            icon={ClockIcon}
            onClick={() => onNavigate?.("app-version-management")}
          />
          <QuickAction
            label="Мониторинг"
            desc="Real-time backend event stream."
            icon={ArrowTrendingUpIcon}
            onClick={() => onNavigate?.("monitoring-dashboard")}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminHome;
